// ============================================================
// FCM Service - Firebase Cloud Messaging Token Yönetimi
// PR Manager - İşletmeci push bildirim sistemi
// ============================================================
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { doc, setDoc, getDoc, getDocs, collection, deleteDoc, arrayUnion, arrayRemove, deleteField } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import app, { db, auth } from './firebase';
import { getDeviceInfo, getOrCreateDeviceId } from '../utils/deviceHelper';

let messaging = null;
let cachedAudioBuffer = null;
let audioContext = null;
let activeSources = []; // Hepsini takip etmek için dizi, böylece race condition da oluşanlar da susar
let activeAudioElements = [];

// Messaging'i lazy ve güvenli init et (her tarayıcı veya ortamda desteklenmeyebilir)
const getMessagingInstance = async () => {
  if (Capacitor.isNativePlatform() || typeof window === 'undefined') {
    return null; // Native uygulamada PushNotifications / LocalNotifications kullanılır, web SDK çağrılmaz
  }
  if (!messaging && app) {
    try {
      const supported = await isSupported();
      if (supported) {
        messaging = getMessaging(app);
      } else {
        return null;
      }
    } catch (err) {
      console.warn('⚠️ FCM messaging not supported or failed to init:', err?.message || err);
      return null;
    }
  }
  return messaging;
};

// ──────────────────────────────────────────────
// VAPID KEY - Firebase Console'dan alınan key
// Kullanıcı key'i .env'e ekleyince buradan okunur
// ──────────────────────────────────────────────
// ──────────────────────────────────────────────
// Güvenli Token Anahtarı Dönüştürücü (Firestore nested field hatasını önlemek için)
// ──────────────────────────────────────────────
export const getSafeTokenKey = (token) => {
  if (!token) return '';
  return token.replace(/[^a-zA-Z0-9]/g, '_');
};

const VAPID_KEY = process.env.REACT_APP_VAPID_KEY;

// ─── APP BADGE MANAJMANI (PWA & Native App Icon Red Count) ───
export const updateAppBadge = async (count) => {
  try {
    const badgeCount = Math.max(0, Number(count) || 0);
    
    // 1. Native Capacitor (Android / iOS)
    if (Capacitor.isNativePlatform()) {
      try {
        await PushNotifications.setBadge({ count: badgeCount });
      } catch (cErr) {
        console.warn('⚠️ Native badge update failed:', cErr.message);
      }
    }

    // 2. Web App Badging API (PWA Chrome/Safari)
    if ('setAppBadge' in navigator) {
      if (badgeCount > 0) {
        await navigator.setAppBadge(badgeCount);
      } else {
        await navigator.clearAppBadge();
      }
    }
  } catch (err) {
    // Silent fail if browser doesn't support or permission not granted
  }
};

export const clearAppBadge = async () => {
  await updateAppBadge(0);
};

// ──────────────────────────────────────────────
// BİLDİRİM İZNİ İSTE
// ──────────────────────────────────────────────
export const requestNotificationPermission = async () => {
  if (Capacitor.isNativePlatform()) {
    const status = await PushNotifications.requestPermissions();
    return status.receive === 'granted';
  }

  if (!('Notification' in window)) {
    console.warn('⚠️ Bu tarayıcı bildirimleri desteklemiyor');
    return false;
  }

  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') {
    console.warn('⚠️ Bildirim izni daha önce reddedildi');
    return false;
  }

  const permission = await Notification.requestPermission();
  return permission === 'granted';
};

// ──────────────────────────────────────────────
// FCM TOKEN AL ve Firebase'e KAYDET
// Login olan işletmecinin tüm cihazlarına token kaydedilir
// ──────────────────────────────────────────────
export const initFCMForUser = async (userEmail, businessIds = []) => {
  // Kullanıcı emaili yoksa localStorage'dan veya aktif kullanıcıdan al
  if (!userEmail) {
    const savedUser = JSON.parse(localStorage.getItem('user') || '{}');
    userEmail = savedUser?.email || auth.currentUser?.email;
  }

  if (!userEmail) {
    console.warn('⚠️ FCM: Kullanıcı e-postası bulunamadı, token kaydı erteleniyor.');
    return null;
  }

  // Firebase auth oturumunun oturmasını bekle (özellikle mobil app açılışında)
  if (!auth.currentUser) {
    try {
      await new Promise((resolve) => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
          unsubscribe();
          resolve(user);
        });
        setTimeout(() => resolve(null), 4000); // En fazla 4 sn bekle
      });
    } catch (authWaitErr) {
      console.warn('⚠️ FCM auth wait err:', authWaitErr);
    }
  }

  // 📱 NATIVE ANDROID/iOS LOGIC
  if (Capacitor.isNativePlatform()) {
    console.log('📱 NATIVE PUSH: Native platform detected, initializing Capacitor Push for:', userEmail);
    
    // 🔔 Bildirim izin durumunu kontrol et ve iste
    try {
      let permStatus = await PushNotifications.checkPermissions();
      if (permStatus.receive !== 'granted') {
        permStatus = await PushNotifications.requestPermissions();
      }
      if (permStatus.receive !== 'granted') {
        console.warn('⚠️ NATIVE PUSH: Kullanıcı bildirim iznini reddetti.');
        return null;
      }
    } catch (permErr) {
      console.warn('⚠️ NATIVE PUSH: İzin kontrol hatası:', permErr);
    }

    // 🔔 Kanalları kontrol et ve eksik olanları oluştur (Kullanıcı ayarlarını sıfırlamamak için deleteChannel YAPILMAZ)
    try {
      console.log('📱 NATIVE PUSH: Checking notification channels...');
      const existingChannelsRes = await PushNotifications.listChannels();
      const existingIds = new Set((existingChannelsRes?.channels || []).map(c => c.id));

      const channelsToEnsure = [
        {
          id: 'orders-v1',
          name: 'Sipariş Bildirimleri',
          description: 'Yeni ve bekleyen sipariş bildirimleri',
          importance: 5,
          visibility: 1,
          vibration: true,
          lights: true,
          lightColor: '#3B82F6',
          sound: 'siparisbildirimi'
        },
        {
          id: 'whatsapp-v1',
          name: 'WhatsApp Mesajları',
          description: 'Gelen yeni WhatsApp mesaj bildirimleri',
          importance: 4,
          visibility: 1,
          vibration: true,
          lights: true,
          lightColor: '#25D366',
          sound: 'whatsapp_message'
        },
        {
          id: 'courier-v1',
          name: 'Kurye Görevleri',
          description: 'Kurye atama ve teslimat bildirimleri',
          importance: 5,
          visibility: 1,
          vibration: true,
          lights: true,
          lightColor: '#F59E0B',
          sound: 'siparisbildirimi'
        },
        {
          id: 'customer-v1',
          name: 'Müşteri Güncellemeleri',
          description: 'Sipariş durum ve teklif bildirimleri',
          importance: 4,
          visibility: 1,
          vibration: true,
          lights: true,
          lightColor: '#10B981',
          sound: 'default'
        },
        {
          id: 'system-v1',
          name: 'Sistem Bildirimleri',
          description: 'Genel sistem ve hesap bildirimleri',
          importance: 3,
          visibility: 1,
          vibration: false,
          lights: false,
          sound: 'default'
        }
      ];

      for (const ch of channelsToEnsure) {
        if (!existingIds.has(ch.id)) {
          await PushNotifications.createChannel(ch);
          console.log(`✅ NATIVE PUSH: Created channel ${ch.id}`);
        }
      }
      console.log('✅ NATIVE PUSH: Notification channels verified');
    } catch (channelErr) {
      console.warn('⚠️ NATIVE PUSH: Channel creation/verification failed:', channelErr);
    }
    
    return new Promise(async (resolve, reject) => {
      try {
        console.log('📱 NATIVE PUSH: Adding registration listener...');
        
        const registerListener = await PushNotifications.addListener('registration', async (tokenResponse) => {
          const token = tokenResponse.value;
          console.log('✅ NATIVE PUSH: FCM Token received:', token.substring(0, 30) + '...');
          
          const deviceInfo = getDeviceInfo();
          const deviceId = deviceInfo.deviceId;

          const userId = userEmail.replace(/[@.]/g, '_');
          const userDocRef = doc(db, 'business_accounts', userId);
          const legacyUserDocRef = doc(db, 'users', userEmail);
          const deviceDocRef = doc(db, 'business_accounts', userId, 'devices', deviceId);

          console.log('📱 NATIVE PUSH: Registering device to Firestore:', deviceId, 'user:', userId);
          
          try {
            const safeKey = getSafeTokenKey(token);

            // 1. Yeni MİMARİ: devices/{deviceId} alt koleksiyonuna tekil kayıt
            await setDoc(deviceDocRef, {
              deviceId,
              fcmToken: token,
              deviceName: deviceInfo.deviceName,
              osName: deviceInfo.osName,
              browserName: deviceInfo.browserName,
              platform: deviceInfo.platform,
              userAgent: deviceInfo.userAgent,
              lastActive: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              isRevoked: false
            }, { merge: true });

            // 2. Geriye Dönük Uyumluluk: business_accounts ana dokümanı
            await setDoc(userDocRef, {
              fcmTokens: arrayUnion(token),
              [`deviceDetails.${safeKey}`]: {
                deviceId,
                device: deviceInfo.deviceName,
                browser: deviceInfo.browserName,
                platform: deviceInfo.platform,
                lastActive: new Date().toISOString(),
                userAgent: deviceInfo.userAgent
              },
              lastTokenUpdate: new Date().toISOString(),
              email: userEmail,
              businessIds: businessIds
            }, { merge: true });

            // 3. Legacy users collection sync
            try {
              await setDoc(legacyUserDocRef, {
                fcmTokens: arrayUnion(token),
                [`deviceDetails.${safeKey}`]: {
                  deviceId,
                  device: deviceInfo.deviceName,
                  browser: deviceInfo.browserName,
                  platform: deviceInfo.platform,
                  lastActive: new Date().toISOString()
                },
                lastTokenUpdate: new Date().toISOString(),
                email: userEmail
              }, { merge: true });
            } catch (legacyErr) {
              console.warn('⚠️ Legacy users sync failed:', legacyErr);
            }

            // 4. İşletmelerin kendi users alt koleksiyonlarına da token'ı senkronize et
            if (Array.isArray(businessIds) && businessIds.length > 0) {
              for (const bizId of businessIds) {
                if (!bizId || bizId === 'all') continue;
                try {
                  const bizUserRef = doc(db, 'businesses', 'food_beverage', 'businesses', bizId, 'users', userEmail);
                  await setDoc(bizUserRef, {
                    fcmTokens: arrayUnion(token),
                    [`deviceDetails.${safeKey}`]: deviceInfo,
                    lastTokenUpdate: new Date().toISOString(),
                    email: userEmail
                  }, { merge: true });
                } catch (bizErr) {}
              }
            }

          } catch (dbErr) {
            console.warn('⚠️ NATIVE PUSH: Firestore token kaydı hatası:', dbErr.message);
          }

          console.log('✅ NATIVE PUSH: Firestore registration COMPLETE');
          localStorage.setItem('fcm_token', token);
          
          try {
            await PushNotifications.setPresentationOptions({
              presentationOptions: ['badge', 'sound', 'alert']
            });
          } catch (e) {}
          
          registerListener.remove();
          resolve(token);
        });

        const errorListener = await PushNotifications.addListener('registrationError', (error) => {
          console.error('❌ NATIVE PUSH: Registration error event:', error.error);
          errorListener.remove();
          reject(error.error);
        });

        console.log('📱 NATIVE PUSH: Calling PushNotifications.register()...');
        
        const handleNotificationDeepLink = (data) => {
          if (!data) return;
          console.log('🔗 NATIVE PUSH: Handling notification click payload:', data);
          const orderId = data.orderId;
          const businessId = data.businessId;
          const eventType = data.eventType || data.type;
          const isWhatsApp = eventType === 'whatsapp.inbound' || eventType === 'whatsapp_message' || !!data.senderPhone;

          if (orderId) {
            console.log('🚀 NATIVE PUSH: Deep linking to order:', orderId, 'Business:', businessId);
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.set('openOrder', orderId);
            if (businessId) {
              searchParams.set('businessId', businessId);
            }
            window.location.href = '/sectors/food-beverage/dashboard?' + searchParams.toString();
          } else if (isWhatsApp) {
            const senderPhone = data.senderPhone || '';
            const cleanPhone = senderPhone.replace(/\D/g, '');
            console.log('💬 NATIVE PUSH: Deep linking to WhatsApp chat:', senderPhone);
            const targetUrl = cleanPhone ? `/business/whatsapp?phone=${cleanPhone}` : '/business/whatsapp';
            window.location.href = targetUrl;
          } else if (data.url) {
            window.location.href = data.url;
          }
        };

        // 🔗 Arka plan/sistem bildirim tıklamasını dinle
        PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
          console.log('🔗 NATIVE PUSH: Action performed (clicked):', notification);
          handleNotificationDeepLink(notification.notification?.data);
        });

        // 🔗 Ön plan yerel bildirim kartına tıklamayı dinle
        LocalNotifications.addListener('localNotificationActionPerformed', (notificationAction) => {
          console.log('🔗 LOCAL NOTIF: Action performed (clicked):', notificationAction);
          handleNotificationDeepLink(notificationAction.notification?.extra);
        });

        await PushNotifications.register();
      } catch (err) {
        console.error('❌ NATIVE PUSH: Catch block error:', err);
        reject(err);
      }
    });
  }

  // 🌐 WEB LOGIC
  if (!VAPID_KEY) {
    console.warn('⚠️ REACT_APP_VAPID_KEY .env dosyasında tanımlı değil. Push bildirimler çalışmayacak.');
    return null;
  }

  const msg = await getMessagingInstance();
  if (!msg) return null;

  try {
    // Service worker kayıtlı mı kontrol et
    if (!('serviceWorker' in navigator)) {
      console.warn('⚠️ Service Worker desteklenmiyor');
      return null;
    }

    // Find active unified service worker registration
    let registration = null;
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      registration = registrations.find(r => r.active) || null;
      if (!registration) {
        registration = await navigator.serviceWorker.ready;
      }
    } catch (swErr) {
      console.warn('⚠️ Could not fetch active SW:', swErr);
    }

    // FCM Token al (varsa özel registration'ı aktar, yoksa default'u kullan)
    const token = await getToken(msg, { 
      vapidKey: VAPID_KEY,
      ...(registration ? { serviceWorkerRegistration: registration } : {})
    });

    if (!token) {
      console.warn('⚠️ FCM token alınamadı');
      return null;
    }

    // Cihaz Bilgilerini Al
    const deviceInfo = getDeviceInfo();
    const deviceId = deviceInfo.deviceId;

    if (process.env.NODE_ENV === 'development') {
      console.log('✅ FCM Token alındı:', token.substring(0, 20) + '...', 'DeviceId:', deviceId);
    }

    // Token'ı Firestore'a kaydet
    const userId = userEmail.replace(/[@.]/g, '_');
    const userDocRef = doc(db, 'business_accounts', userId);
    const legacyUserDocRef = doc(db, 'users', userEmail);
    const deviceDocRef = doc(db, 'business_accounts', userId, 'devices', deviceId);
    const safeKey = getSafeTokenKey(token);

    // 1. Yeni MİMARİ: devices/{deviceId} alt koleksiyonuna tekil kayıt
    try {
      await setDoc(deviceDocRef, {
        deviceId,
        fcmToken: token,
        deviceName: deviceInfo.deviceName,
        osName: deviceInfo.osName,
        browserName: deviceInfo.browserName,
        platform: deviceInfo.platform,
        userAgent: deviceInfo.userAgent,
        screenResolution: deviceInfo.screenResolution,
        lastActive: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isRevoked: false
      }, { merge: true });
    } catch (devSubErr) {
      console.warn('⚠️ devices sub-collection write error:', devSubErr.message);
    }

    // 2. Geriye Dönük Uyumluluk: business_accounts ana dokümanı
    await setDoc(userDocRef, {
      fcmTokens: arrayUnion(token),
      [`deviceDetails.${safeKey}`]: {
        deviceId,
        device: deviceInfo.deviceName,
        browser: deviceInfo.browserName,
        platform: deviceInfo.platform,
        lastActive: new Date().toISOString(),
        userAgent: deviceInfo.userAgent
      },
      lastTokenUpdate: new Date().toISOString(),
      email: userEmail,
      businessIds: businessIds
    }, { merge: true });

    // 3. Legacy users collection sync
    try {
      await setDoc(legacyUserDocRef, {
        fcmTokens: arrayUnion(token),
        [`deviceDetails.${safeKey}`]: {
          deviceId,
          device: deviceInfo.deviceName,
          browser: deviceInfo.browserName,
          platform: deviceInfo.platform,
          lastActive: new Date().toISOString()
        },
        lastTokenUpdate: new Date().toISOString(),
        email: userEmail
      }, { merge: true });
    } catch (legacyErr) {
      console.warn('⚠️ Legacy users collection sync failed:', legacyErr);
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('✅ FCM Token ve Cihaz Bilgisi Firestore\'a kaydedildi.');
    }
    localStorage.setItem('fcm_token', token);
    return token;

  } catch (err) {
    if (err.code === 'messaging/permission-blocked') {
      console.warn('⚠️ Bildirim izni engellendi.');
    } else if (err.code === 'permission-denied' || err.message?.includes('permission')) {
      console.warn('⚠️ FCM: Firestore token kaydı yetki hatası (sessizce atlanıyor):', err.message);
    } else {
      console.error('❌ FCM Token alma hatası:', err);
    }
    return null;
  }
};

// ──────────────────────────────────────────────
// KAYITLI CİHAZLARI GETİR
// ──────────────────────────────────────────────
export const getMyDevices = async (userEmail) => {
  try {
    const userId = userEmail.replace(/[@.]/g, '_');
    const currentDeviceId = getOrCreateDeviceId();
    const devicesList = [];
    const seenDeviceIds = new Set();

    // 1. Yeni MİMARİ: devices/{deviceId} alt koleksiyonunu oku
    try {
      const devicesCollRef = collection(db, 'business_accounts', userId, 'devices');
      const devicesSnap = await getDocs(devicesCollRef);
      
      devicesSnap.forEach(dDoc => {
        const dData = dDoc.data();
        if (dData.isRevoked) return; // İptal edilmişse gösterme
        
        seenDeviceIds.add(dData.deviceId || dDoc.id);
        devicesList.push({
          id: dDoc.id,
          deviceId: dData.deviceId || dDoc.id,
          device: dData.deviceName || 'Bilinmeyen Cihaz',
          browser: dData.browserName || dData.platform || 'Tarayıcı',
          platform: dData.platform || 'web',
          osName: dData.osName || '',
          token: dData.fcmToken || '',
          lastActive: dData.lastActive || dData.updatedAt || new Date().toISOString(),
          isCurrentDevice: (dData.deviceId === currentDeviceId)
        });
      });
    } catch (subCollErr) {
      console.warn('⚠️ devices sub-collection read warning:', subCollErr.message);
    }

    // 2. Geriye Dönük Uyumluluk: Ana dokümandaki eski kayıtları harmanla (varsa ve sub-collection'da yoksa)
    const userDocRef = doc(db, 'business_accounts', userId);
    const docSnap = await getDoc(userDocRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      const tokens = data.fcmTokens || [];
      const details = data.deviceDetails || {};
      
      tokens.forEach(token => {
        const tokenKey = getSafeTokenKey(token);
        const deviceDetail = details[tokenKey] || {};
        const devId = deviceDetail.deviceId || ('legacy_' + tokenKey.substring(0, 12));
        
        // Eğer bu cihaz zaten alt koleksiyondan gelmediyse ekle
        if (!seenDeviceIds.has(devId)) {
          seenDeviceIds.add(devId);
          devicesList.push({
            id: devId,
            deviceId: devId,
            token,
            device: deviceDetail.device || 'Kayıtlı Cihaz',
            browser: deviceDetail.browser || 'Bildirim Alıcısı',
            platform: deviceDetail.platform || 'web',
            osName: '',
            lastActive: deviceDetail.lastActive || data.lastTokenUpdate || new Date().toISOString(),
            isCurrentDevice: false,
            isLegacy: true
          });
        }
      });
    }

    // Son aktiviteye göre sırala (en güncel en üstte)
    return devicesList.sort((a, b) => new Date(b.lastActive) - new Date(a.lastActive));
  } catch (error) {
    console.error('❌ Cihazlar getirilemedi:', error);
    return [];
  }
};

// ──────────────────────────────────────────────
// CİHAZ (OTURUM / TOKEN) SİL
// ──────────────────────────────────────────────
export const removeDevice = async (userEmail, deviceOrToken) => {
  try {
    const userId = userEmail.replace(/[@.]/g, '_');
    const userDocRef = doc(db, 'business_accounts', userId);
    
    // 1. Eğer deviceId geldiyse alt koleksiyondan sil
    try {
      const deviceDocRef = doc(db, 'business_accounts', userId, 'devices', deviceOrToken);
      await deleteDoc(deviceDocRef);
    } catch (e) {
      // deviceOrToken bir token olabilir
    }

    // 2. Ana dokümandan token temizliği yap
    const docSnap = await getDoc(userDocRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      const tokens = data.fcmTokens || [];
      const details = data.deviceDetails || {};

      // Eşleşen token'ları bul
      let matchedTokens = [];
      if (tokens.includes(deviceOrToken)) {
        matchedTokens.push(deviceOrToken);
      } else {
        // deviceId'ye göre bul
        for (const [tKey, detail] of Object.entries(details)) {
          if (detail.deviceId === deviceOrToken) {
            const tok = tokens.find(t => getSafeTokenKey(t) === tKey);
            if (tok) matchedTokens.push(tok);
          }
        }
      }

      const updatePayload = {};
      matchedTokens.forEach(t => {
        updatePayload.fcmTokens = arrayRemove(t);
        updatePayload[`deviceDetails.${getSafeTokenKey(t)}`] = deleteField();
      });

      if (Object.keys(updatePayload).length > 0) {
        await setDoc(userDocRef, updatePayload, { merge: true });
      }
    }
    
    console.log('✅ Cihaz başarıyla silindi/yetkisi kaldırıldı.');
    return true;
  } catch (error) {
    console.error('❌ Cihaz silinemedi:', error);
    return false;
  }
};

// ──────────────────────────────────────────────
// SES ÇAL (Bildirim Geldiğinde)
// ──────────────────────────────────────────────
let isFetchingAudio = false;
let isStopRequested = false;

let cachedAudioBuffers = {};

export const playNotificationSound = async (loop = false, soundType = 'new_order') => {
  try {
    // 1. Ses Ayarları Kontrolü
    const soundEnabled = localStorage.getItem('order_sound_enabled') !== 'false';
    if (!soundEnabled) return;

    let categorySettings = {};
    try {
      const savedCats = localStorage.getItem('category_sound_settings');
      if (savedCats) categorySettings = JSON.parse(savedCats);
    } catch (e) {}

    if (categorySettings[soundType] === false) return;

    let volume = 0.8;
    const savedVol = localStorage.getItem('order_sound_volume');
    if (savedVol !== null) {
      const p = parseFloat(savedVol);
      if (!isNaN(p)) volume = p;
    }
    if (volume <= 0) return;

    const SOUND_URL = soundType === 'whatsapp_message' ? '/sounds/whatsapp_message.mp3' : '/sounds/new_order.mp3';
    console.log(`🔊 Playing notification sound (type: ${soundType}, loop: ${loop}, vol: ${volume}):`, SOUND_URL);
    
    // Önce çalan bir şey varsa durdur
    stopNotificationSound();
    isStopRequested = false;

    // 1. AudioContext (WebAudio) - En stabil yol
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (AudioContext) {
      if (!audioContext) audioContext = new AudioContext();
      
      if (audioContext.state === 'suspended' || audioContext.state === 'interrupted') {
        try {
          await audioContext.resume();
          console.log('🔊 AudioContext resumed successfully');
        } catch (resErr) {
          console.warn('⚠️ AudioContext resume failed:', resErr);
        }
      }

      if (!cachedAudioBuffers[SOUND_URL]) {
        try {
          isFetchingAudio = true;
          const response = await fetch(SOUND_URL);
          const arrayBuffer = await response.arrayBuffer();
          cachedAudioBuffers[SOUND_URL] = await audioContext.decodeAudioData(arrayBuffer);
        } catch (fetchErr) {
          console.warn('⚠️ Could not cache audio buffer:', fetchErr);
        } finally {
          isFetchingAudio = false;
        }
      }

      if (cachedAudioBuffers[SOUND_URL]) {
        if (isStopRequested) {
            console.log('🛑 Stop aborted playNotificationSound just before buffer playing.');
            return;
        }

        const gainNode = audioContext.createGain();
        gainNode.gain.setValueAtTime(volume, audioContext.currentTime);

        const source = audioContext.createBufferSource();
        source.buffer = cachedAudioBuffers[SOUND_URL];
        source.loop = loop;
        source.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        source.start(0);
        activeSources.push(source);
        console.log('✅ Sound played via AudioContext with volume:', volume);
        return;
      }
    }
    
    if (isStopRequested) return;

    // 2. Fallback: Standart HTML5 Audio
    const audio = new Audio(SOUND_URL);
    audio.volume = volume;
    audio.loop = loop;
    audio.play().catch(e => console.warn('HTML5 Audio failed:', e));
    activeAudioElements.push(audio);

  } catch (err) {
    console.warn('⚠️ Sound play failed:', err);
  }
};

/**
 * Aktif çalan bildirim sesini susturur
 */
export const stopNotificationSound = () => {
  isStopRequested = true;
  activeSources.forEach(source => {
    try {
      source.stop();
    } catch (e) {}
  });
  activeSources = [];
  console.log('🔇 AudioContext sounds stopped.');
  
  activeAudioElements.forEach(audio => {
    try {
      audio.pause();
      audio.currentTime = 0;
    } catch (e) {}
  });
  activeAudioElements = [];
  console.log('🔇 HTML5 Audios stopped.');
};

// Pre-load sound metadata/buffer silently if browser allows (optional)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  console.log('🔇 Notification service ready.');
}


// ──────────────────────────────────────────────
// ÖN PLANDA BİLDİRİM DİNLE
// Uygulama açıkken gelen FCM mesajı yakalar
// (Arka planda firebase-messaging-sw.js halleder)
// ──────────────────────────────────────────────
export const setupForegroundNotifications = (onNewOrderCallback, userRole = null) => {
  const path = window.location.pathname;
  const isCustomerPage = path.includes('/menu/') || path.includes('/track/') || path.includes('/qr/menu');

  // 1. WEB (Firebase JS SDK)
  let webUnsubscribe = () => {};

  getMessagingInstance().then(msg => {
    if (msg) {
      webUnsubscribe = onMessage(msg, (payload) => {
        console.log('📬 Web Foreground Message:', payload);
        const { title, body } = payload.notification || {};
        const data = payload.data || {};
        // Role-based sound logic
        const isCourier = userRole === 'courier' || window.location.pathname.includes('/courier-app');
        
        const currentPath = window.location.pathname;
        const isCustomerPageNow = currentPath.includes('/menu/') || currentPath.includes('/track/') || currentPath.includes('/qr/menu') || currentPath.includes('/order-tracking');
        
        let shouldPlaySound = !isCustomerPageNow;
        let loopSound = true;

        const eventType = data.eventType || data.type;
        const isCorporateEmail = eventType === 'corporate_email' || data.type === 'corporate_email';
        const isWhatsApp = eventType === 'whatsapp.inbound' || eventType === 'whatsapp_message' || !!data.senderPhone;
        const isOrder = !isCorporateEmail && (eventType === 'order.new' || eventType === 'new_order' || (!isWhatsApp && (data.orderId || (title && title.toLowerCase().includes('sipariş')))));

        if (isCorporateEmail) {
          // Kurumsal e-postalarda sipariş alarmı çalınmaz (sessiz bildirim)
          shouldPlaySound = false;
          loopSound = false;
        } else if (isWhatsApp) {
          // WhatsApp mesajı tek seferlik çalar, döngüye girmez
          loopSound = false;
        } else if (isCourier) {
          const isAssignment = data.type === 'assignment' || data.action === 'assigned' || (title && title.toLowerCase().includes('atandı')) || (body && body.toLowerCase().includes('atandı'));
          const isNewUnassigned = data.type === 'new_unassigned' || (body && body.toLowerCase().includes('havuz')) || (title && title.toLowerCase().includes('onaylandı')) || (body && body.toLowerCase().includes('onaylandı'));
          
          if (!isAssignment && !isNewUnassigned) {
            shouldPlaySound = false; // SILENCE generic new orders for couriers
            loopSound = false; 
            console.log('🔇 FCM: Generic order silenced for courier');
          } else {
            console.log('🔊 FCM: Assignment or Unassigned order sound trigger for courier');
          }
        }

        // İptal veya durum değişikliklerinde FCM push bildirimi gelse dahi ses çalınmasın
        const isCancellation = (title && title.includes('İptal')) || (body && body.includes('iptal'));
        const isStatusUpdate = data.type === 'status_update' || data.action === 'status_changed';
        
        if (isCancellation || isStatusUpdate) {
          shouldPlaySound = false;
        }

        if (shouldPlaySound) {
          const soundType = isWhatsApp ? 'whatsapp_message' : 'new_order';
          playNotificationSound(loopSound, soundType);
        }

        if (isOrder && onNewOrderCallback) {
          onNewOrderCallback({
            title: title || '🔔 Yeni Sipariş!',
            body: body || 'Yeni sipariş geldi.',
            brandName: data.brandName || '',
            businessId: data.businessId || '',
            orderId: data.orderId || '',
            url: data.url || '/sectors/food-beverage/orders/order-management'
          });
        }
      });
    }
  }).catch(e => {
    console.debug('FCM foreground listener not active:', e?.message || e);
  });

  // 2. NATIVE (Capacitor)
  let nativeListener = { remove: () => {} };
  const recentForegroundEvents = new Set();

  if (Capacitor.isNativePlatform()) {
    PushNotifications.addListener('pushNotificationReceived', async (notification) => {
      console.log('📬 Native Foreground Notification Received:', notification);
      
      const data = notification.data || {};
      const eventId = data.eventId || `${data.eventType || data.type || 'evt'}_${data.orderId || data.senderPhone || Date.now()}`;
      if (recentForegroundEvents.has(eventId)) {
        console.log('🛑 Native Foreground: Event already processed, skipping:', eventId);
        return;
      }
      recentForegroundEvents.add(eventId);
      if (recentForegroundEvents.size > 100) {
        const oldest = recentForegroundEvents.values().next().value;
        recentForegroundEvents.delete(oldest);
      }

      const eventType = data.eventType || data.type;
      const isWhatsApp = eventType === 'whatsapp.inbound' || eventType === 'whatsapp_message' || !!data.senderPhone;
      const isOrder = eventType === 'order.new' || eventType === 'new_order' || (!isWhatsApp && (data.orderId || notification.title?.toLowerCase().includes('sipariş')));
      const isCourier = userRole === 'courier' || window.location.pathname.includes('/courier-app');

      // Android'de foreground'da sistem bildirimi ve kanal sesini LocalNotifications ile göster
      try {
        const channelId = notification.channelId || (isWhatsApp ? 'whatsapp-v1' : (isCourier ? 'courier-v1' : 'orders-v1'));
        const localNotifId = Math.floor(Math.random() * 1000000);
        
        await LocalNotifications.schedule({
          notifications: [
            {
              id: localNotifId,
              title: notification.title || (isWhatsApp ? 'WhatsApp Mesajı' : 'Yeni Sipariş!'),
              body: notification.body || '',
              channelId: channelId,
              extra: data,
              smallIcon: 'ic_launcher',
              iconColor: isWhatsApp ? '#25D366' : '#3B82F6'
            }
          ]
        });
        console.log(`🔔 Native Foreground: LocalNotification scheduled on channel: ${channelId}`);
      } catch (localErr) {
        console.warn('⚠️ Native Foreground LocalNotification failed:', localErr);
      }

      // WebAudio / UI bildirim sesi yönetimi
      let shouldPlaySound = !isCustomerPage;
      let loopSound = true;

      const isCorporateEmail = eventType === 'corporate_email' || data.type === 'corporate_email';

      if (isCorporateEmail) {
        // Kurumsal e-postalarda sipariş sesi çalınmaz
        shouldPlaySound = false;
        loopSound = false;
      } else if (isWhatsApp) {
        // WhatsApp mesajı tek seferlik çalar, döngüye girmez
        loopSound = false;
      } else if (isCourier) {
        const title = notification.title || '';
        const body = notification.body || '';
        const isAssignment = data.type === 'assignment' || data.action === 'assigned' || title.toLowerCase().includes('atandı') || body.toLowerCase().includes('atandı');
        const isNewUnassigned = data.type === 'new_unassigned' || body.toLowerCase().includes('havuz') || title.toLowerCase().includes('onaylandı') || body.toLowerCase().includes('onaylandı');
        
        if (!isAssignment && !isNewUnassigned) {
          shouldPlaySound = false; // SILENCE generic new orders for couriers
          loopSound = false;
          console.log('🔇 FCM Native: Generic order silenced for courier');
        } else {
          console.log('🔊 FCM Native: Relevant order sound trigger for courier');
        }
      }

      // Ön planda ses çal (Native ve Web ortamında)
      if (shouldPlaySound) {
        const soundType = isWhatsApp ? 'whatsapp_message' : 'new_order';
        playNotificationSound(loopSound, soundType);
      }
      
      if (isOrder && onNewOrderCallback) {
        onNewOrderCallback({
          title: notification.title || '🔔 Yeni Sipariş!',
          body: notification.body || '',
          brandName: notification.data?.brandName || '',
          orderId: notification.data?.orderId || '',
          url: notification.data?.url || '/sectors/food-beverage/orders/order-management'
        });
      }
    }).then(listener => {
      nativeListener = listener;
    }).catch(err => {
      console.warn('⚠️ PushNotifications listener registration failed:', err);
    });
  }

  return () => {
    webUnsubscribe();
    if (nativeListener && typeof nativeListener.remove === 'function') {
      nativeListener.remove();
    }
  };
};

// ──────────────────────────────────────────────
// TOKEN SİL (Logout olunca)
// ──────────────────────────────────────────────
export const removeFCMToken = async (userEmail) => {
  try {
    const userId = userEmail.replace(/[@.]/g, '_');
    const userDocRef = doc(db, 'business_accounts', userId);
    
    // 1. Try to get token from localStorage (most reliable for both Web & Native)
    let tokenToRemove = localStorage.getItem('fcm_token');
    
    // 2. Fallback to Web SDK if token not in storage and we are on Web
    if (!tokenToRemove && !Capacitor.isNativePlatform()) {
      const msg = getMessagingInstance();
      if (msg && VAPID_KEY) {
        try {
          tokenToRemove = await getToken(msg, { vapidKey: VAPID_KEY });
        } catch (e) {}
      }
    }

    if (!tokenToRemove) {
      console.warn('⚠️ FCM: Silinecek token bulunamadı.');
      return;
    }

    // Firestore'dan token'ı ve cihaz detaylarını kaldır
    const tokenKey = getSafeTokenKey(tokenToRemove);
    await setDoc(userDocRef, { 
      fcmTokens: arrayRemove(tokenToRemove),
      [`deviceDetails.${tokenKey}`]: deleteField()
    }, { merge: true });
    
    localStorage.removeItem('fcm_token');
    console.log('✅ FCM: Token ve cihaz bilgisi Firestore\'dan silindi');
  } catch (err) {
    console.warn('⚠️ FCM Token silme hatası:', err);
  }
};

// ──────────────────────────────────────────────
// QR MİSAFİR VE CARİ İÇİN SADECE TOKEN AL
// ──────────────────────────────────────────────
export const requestAndGetFCMToken = async () => {
  try {
    if (Capacitor.isNativePlatform()) {
      const hasPermission = await requestNotificationPermission();
      if (!hasPermission) return null;
      return localStorage.getItem('fcm_token');
    }

    if (!VAPID_KEY) {
      console.warn('⚠️ REACT_APP_VAPID_KEY tanımlı değil.');
      return null;
    }

    const hasPermission = await requestNotificationPermission();
    if (!hasPermission) return null;

    const msg = getMessagingInstance();
    // Find active unified service worker registration
    let registration = null;
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      registration = registrations.find(r => r.active) || null;
      if (!registration) {
        registration = await navigator.serviceWorker.ready;
      }
    } catch (swErr) {
      console.warn('⚠️ Could not fetch active SW:', swErr);
    }

    const token = await getToken(msg, { 
      vapidKey: VAPID_KEY,
      ...(registration ? { serviceWorkerRegistration: registration } : {})
    });
    if (token) {
      localStorage.setItem('fcm_token', token);
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ FCM Token (Guest) alındı:', token.substring(0, 15) + '...');
      }
      return token;
    }
    return null;
  } catch (err) {
    console.error('❌ requestAndGetFCMToken hatası:', err);
    return null;
  }
};

export default {
  initFCMForUser,
  requestNotificationPermission,
  requestAndGetFCMToken,
  setupForegroundNotifications,
  removeFCMToken,
  playNotificationSound,
  stopNotificationSound
};
